/* Settings avanzado: la cuadrícula rol × módulo × acción (docs/03, api-v7).
 *
 * Lo que se administra aquí es el verbo. El módulo y los clientes se siguen
 * concediendo por usuario en la bandeja de arriba: son dos cosas distintas y
 * la pantalla lo dice, porque confundirlas es cómo se regala una cuenta. */
import { api, type Matriz, type Rol } from "../api";
import { estado } from "../estado";
import { esc, modal, toast, Fechas } from "../ui";
import type { Vista } from "./tipos";

const NOMBRE_MODULO: Record<string, string> = {
  pm: "Project Management", timesheet: "Timesheet", knowledge: "Conocimiento", makai: "Makai",
  finanzas: "Finanzas", settings: "Ajustes", compliance: "Compliance", auditoria: "Auditoría",
};

export async function matriz(render: () => void): Promise<Vista> {
  const m: Matriz = await api.matriz();
  const puedeEditar = estado.puede("settings", "editar");
  const puedeCrear = estado.puede("settings", "crear");
  const puedeBorrar = estado.puede("settings", "eliminar");

  // Los módulos en el orden en que se conceden, con sus verbos.
  const modulos: { modulo: string; acciones: { accion: string; descripcion: string }[] }[] = [];
  for (const a of m.acciones) {
    let g = modulos.find((x) => x.modulo === a.modulo);
    if (!g) { g = { modulo: a.modulo, acciones: [] }; modulos.push(g); }
    g.acciones.push({ accion: a.accion, descripcion: a.descripcion });
  }

  const celda = (r: Rol, modulo: string, accion: string) => {
    const on = r.esSistema || r.celdas.includes(modulo + ":" + accion);
    return '<td class="cx"><input type="checkbox" data-celda="' + modulo + ":" + accion + '"' +
      (on ? " checked" : "") + (r.esSistema || !puedeEditar ? " disabled" : "") + "></td>";
  };

  // Todo lo del rol vive en la columna fija: leer una fila de 37 casillas sin
  // saber de quién es no sirve de nada, y los botones tampoco si hay que
  // desplazarse hasta el final para encontrarlos.
  const fila = (r: Rol) => '<tr data-rol="' + r.id + '">' +
    '<th class="rolc"><b>' + esc(r.nombre) + "</b>" +
      '<div class="rolsub">' + (r.esSistema ? "no se edita" : r.usuarios + (r.usuarios === 1 ? " persona" : " personas")) +
      (r.firmaTipos.length ? " · firma " + r.firmaTipos.length : "") + "</div>" +
      (r.esSistema || !puedeEditar ? "" : '<div class="rolbtns">' +
        '<button class="btn chico primario oculto" data-guardar="' + r.id + '">Guardar</button>' +
        '<button class="btn chico fantasma" data-firma="' + r.id + '">Firma</button>' +
        '<button class="btn chico fantasma" data-rename="' + r.id + '">Nombre</button>' +
        (puedeBorrar ? '<button class="btn chico fantasma" data-borrar="' + r.id + '">Borrar</button>' : "") + "</div>") +
    "</th>" +
    modulos.map((g) => g.acciones.map((a) => celda(r, g.modulo, a.accion)).join("")).join("") + "</tr>";

  return {
    html:
      '<div class="card"><div class="cab"><div class="t">Matriz de permisos</div>' +
        '<span style="font-size:12px;color:var(--text3)">rol × módulo × acción · ninguna celda queda indefinida: lo que no está marcado, está negado</span>' +
        (puedeCrear ? '<button class="btn chico" id="mx-nuevo">+ Rol</button>' : "") + "</div>" +
      '<div style="padding:10px 16px;border-bottom:1px solid var(--divider);font-size:11.5px;color:var(--text3)">' +
        "El <b>módulo</b> y los <b>clientes</b> se conceden por persona, arriba. Aquí se concede el <b>verbo</b>: qué puede hacer un rol dentro del módulo que ya tiene. Las dos tienen que dar que sí. El permiso de <b>dinero</b> sigue siendo por persona, y las cifras se rigen por rol, nunca por pertenencia." + "</div>" +
      (m.huerfanos.length ? '<div class="aviso crit" style="margin:12px 16px"><b>' + m.huerfanos.length + " usuario(s) con un rol que no está en la matriz:</b> " +
        m.huerfanos.map((u) => esc(u.nombre) + " (" + esc(u.rol) + ")").join(", ") + ". Sin rol en la matriz no pueden ningún verbo: crea el rol o muévelos a uno.</div>" : "") +
      '<div class="mx-scroll"><table class="mx">' +
        "<thead><tr><th class='rolc' rowspan='2'>Rol</th>" +
          modulos.map((g) => '<th colspan="' + g.acciones.length + '" class="grupo">' + esc(NOMBRE_MODULO[g.modulo] ?? g.modulo) + "</th>").join("") +
"</tr><tr>" +
          modulos.map((g) => g.acciones.map((a) => '<th class="verbo" title="' + esc(a.descripcion) + '">' + esc(a.accion) + "</th>").join("")).join("") +
        "</tr></thead><tbody>" + m.roles.map(fila).join("") + "</tbody></table></div>" +
      '<div style="padding:10px 16px;border-top:1px solid var(--divider);font-size:11.5px;color:var(--text3)">' +
        "Firmar contenido AI no es una celda de esta cuadrícula: se concede por <b>tipo de contenido</b> (botón <i>Firma AI</i>), y además la persona necesita su permiso propio de firma. Cada cambio queda en el registro de auditoría con quién lo hizo." + "</div></div>",

    despues(root) {
      const filaDe = (id: string) => root.querySelector<HTMLElement>('tr[data-rol="' + id + '"]')!;
      const celdasDe = (id: string) => [...filaDe(id).querySelectorAll<HTMLInputElement>("input[data-celda]:checked")].map((x) => x.dataset.celda!);

      // El botón de guardar aparece cuando la fila cambió: sin cambios no hay
      // nada que registrar, y una escritura vacía ensucia la auditoría.
      root.querySelectorAll<HTMLInputElement>("input[data-celda]").forEach((c) => c.onchange = () => {
        const tr = c.closest("tr")!;
        const rol = m.roles.find((r) => r.id === tr.getAttribute("data-rol"))!;
        const ahora = [...tr.querySelectorAll<HTMLInputElement>("input[data-celda]:checked")].map((x) => x.dataset.celda!).sort().join("|");
        const antes = [...rol.celdas].sort().join("|");
        tr.querySelector("[data-guardar]")?.classList.toggle("oculto", ahora === antes);
      });

      root.querySelectorAll<HTMLButtonElement>("[data-guardar]").forEach((b) => b.onclick = async () => {
        const rol = m.roles.find((r) => r.id === b.dataset.guardar)!;
        try {
          await api.guardarRol(rol.id, { celdas: celdasDe(rol.id), firmaTipos: rol.firmaTipos });
          toast("<b>" + esc(rol.nombre) + " actualizado.</b> Se aplica en la siguiente entrada de esa gente.");
          render();
        } catch (e) { toast("<b>No se guardó.</b> " + esc((e as Error).message)); }
      });

      // Quién firma qué tipo de contenido AI.
      root.querySelectorAll<HTMLButtonElement>("[data-firma]").forEach((b) => b.onclick = () => {
        const rol = m.roles.find((r) => r.id === b.dataset.firma)!;
        const deps = [...new Set(m.tipos.map((t) => t.departamento))];
        modal({
          titulo: "Qué firma " + rol.nombre,
          sub: "Firmar es responder por lo que produjo una máquina. Se concede por tipo de contenido, y la persona además necesita su permiso propio de firma.",
          cuerpo: deps.map((d) => '<div class="lbl">' + esc(d.toUpperCase()) + '</div><div style="display:flex;flex-wrap:wrap;gap:6px">' +
            m.tipos.filter((t) => t.departamento === d).map((t) => '<label class="chip' + (rol.firmaTipos.includes(t.id) ? " on" : "") + '"><input type="checkbox" data-tipo="' + t.id + '"' + (rol.firmaTipos.includes(t.id) ? " checked" : "") + ' style="display:none">' + esc(t.nombre) + "</label>").join("") + "</div>").join(""),
          botones: [{ t: "Cancelar" }, { t: "Guardar", cls: "primario", fn: async (mm) => {
            const tipos = [...mm.querySelectorAll<HTMLInputElement>("[data-tipo]:checked")].map((x) => x.dataset.tipo!);
            await api.guardarRol(rol.id, { celdas: rol.celdas, firmaTipos: tipos });
            toast("<b>" + esc(rol.nombre) + "</b> firma " + tipos.length + " tipo(s) de contenido."); render();
          } }],
          alMontar: (mm) => mm.querySelectorAll<HTMLElement>("label.chip").forEach((l) => l.onclick = () =>
            setTimeout(() => l.classList.toggle("on", (l.querySelector("input") as HTMLInputElement).checked), 0)),
        });
      });

      root.querySelectorAll<HTMLButtonElement>("[data-rename]").forEach((b) => b.onclick = () => {
        const rol = m.roles.find((r) => r.id === b.dataset.rename)!;
        modal({
          titulo: "Rol · " + rol.nombre,
          sub: rol.usuarios ? "Renombrarlo arrastra a las " + rol.usuarios + " persona(s) que lo tienen: nadie se queda sin verbos." : "Todavía no lo tiene nadie.",
          cuerpo: '<div class="lbl">NOMBRE</div><input id="mx-n" value="' + esc(rol.nombre) + '">' +
            '<div class="lbl">PARA QUÉ SIRVE (OPCIONAL)</div><textarea id="mx-d" rows="3">' + esc(rol.descripcion || "") + "</textarea>" +
            '<div style="font-size:11.5px;color:var(--text3);margin-top:10px">Última edición: ' + (rol.actualizadoPor ? esc(rol.actualizadoPor) + " · " : "") + esc(Fechas.relativa(rol.actualizadoEn)) + "</div>",
          botones: [{ t: "Cancelar" }, { t: "Guardar", cls: "primario", fn: async (mm) => {
            const nombre = mm.querySelector<HTMLInputElement>("#mx-n")!.value.trim();
            if (!nombre) { toast("El rol necesita nombre."); return false; }
            await api.guardarRol(rol.id, { nombre, descripcion: mm.querySelector<HTMLTextAreaElement>("#mx-d")!.value.trim() || null, celdas: rol.celdas, firmaTipos: rol.firmaTipos });
            toast("Rol actualizado."); render();
          } }],
        });
      });

      root.querySelectorAll<HTMLButtonElement>("[data-borrar]").forEach((b) => b.onclick = () => {
        const rol = m.roles.find((r) => r.id === b.dataset.borrar)!;
        modal({ titulo: "Borrar el rol " + rol.nombre + "?",
          sub: rol.usuarios ? "No se puede: lo usan " + rol.usuarios + " persona(s). Muévelas de rol primero." : "No lo tiene nadie. Queda en el registro de auditoría.",
          botones: rol.usuarios ? [{ t: "Entendido" }] : [{ t: "Cancelar" }, { t: "Borrar", cls: "oscuro", fn: async () => {
            await api.borrarRol(rol.id); toast("Rol borrado."); render();
          } }] });
      });

      const nuevo = root.querySelector<HTMLButtonElement>("#mx-nuevo");
      if (nuevo) nuevo.onclick = () => modal({
        titulo: "Nuevo rol", sub: "Nace sin ninguna celda concedida. Copiar de otro es explícito y queda registrado de dónde salió.",
        cuerpo: '<div class="lbl">NOMBRE</div><input id="mx-nn" placeholder="Por ejemplo: Coordinadora de producción">' +
          '<div class="lbl">PARA QUÉ SIRVE (OPCIONAL)</div><textarea id="mx-nd" rows="2"></textarea>' +
          '<div class="lbl">COPIAR LOS PERMISOS DE</div><select id="mx-nc"><option value="">— empezar en cero —</option>' +
          m.roles.filter((r) => !r.esSistema).map((r) => '<option value="' + r.id + '">' + esc(r.nombre) + "</option>").join("") + "</select>",
        botones: [{ t: "Cancelar" }, { t: "Crear", cls: "primario", fn: async (mm) => {
          const nombre = mm.querySelector<HTMLInputElement>("#mx-nn")!.value.trim();
          if (!nombre) { toast("El rol necesita nombre."); return false; }
          await api.crearRol({ nombre, descripcion: mm.querySelector<HTMLTextAreaElement>("#mx-nd")!.value.trim() || undefined,
            copiarDe: mm.querySelector<HTMLSelectElement>("#mx-nc")!.value || undefined });
          toast("<b>Rol creado.</b> Ahora dile qué puede hacer."); render();
        } }],
      });
    },
  };
}
